// Appel Open Food Facts (objectif § 2 et § 3) : `GET /api/v2/product/<ean>.json`, User-Agent
// identifiant l'app comme leur documentation l'exige (https://openfoodfacts.github.io/openfoodfacts-server/api/#requests),
// et dégradation propre (sans exception) vers le chemin modèle quand la fiche est absente ou que
// `nutriments` est inexploitable. En revanche une panne transitoire (429, 5xx, réseau) LÈVE plutôt
// que de se faire passer pour un « EAN inconnu » (revue PR #549, thread 1317 § 2) : voir le
// commentaire de `lookupOffProduct`. `fetchImpl`/`cache`/`baseUrl` sont injectables pour les tests
// (test/openFoodFacts.test.ts) sans appeler le vrai réseau.
//
// Note navigateur : la spec Fetch retire `User-Agent` de la liste des en-têtes interdits, mais tous
// les navigateurs ne l'honorent pas encore en pratique (certains l'ignorent silencieusement côté
// navigateur, sans erreur). On l'envoie quand même — c'est ce que demande OFF — en sachant que
// l'identification effective dépend du navigateur ; ça ne bloque jamais l'appel.
import { normalizeNutriments } from './normalizeNutriments.js';
import { parseServingGrams } from './servingWeight.js';
import { createProductCache, type CachedProductLookup, type ProductCache } from './productCache.js';
import { offContributeUrl, type OffLookupResult, type OffProduct } from './types.js';

export const OFF_USER_AGENT = 'NutriScan/1.0 (+https://nutriscan.didi-home.com; contact: andy.maddio@gmail.com)';

export interface OffLookupOptions {
  fetchImpl?: typeof fetch;
  cache?: ProductCache;
  baseUrl?: string;
}

let sharedCache: ProductCache | undefined;
function defaultCache(): ProductCache {
  sharedCache ??= createProductCache();
  return sharedCache;
}

interface OffApiResponse {
  status?: number;
  product?: Record<string, unknown>;
}

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function mapOffProduct(ean: string, raw: Record<string, unknown>): OffProduct | null {
  const nutriments = normalizeNutriments(raw.nutriments);
  if (!nutriments) return null;
  const servingSize = readString(raw, 'serving_size');
  return {
    ean,
    productName: readString(raw, 'product_name'),
    nutriments,
    servingSize,
    servingGrams: parseServingGrams(servingSize),
    nutriscoreGrade: readString(raw, 'nutriscore_grade'),
    imageUrl: readString(raw, 'image_url'),
  };
}

/**
 * Cherche un produit par EAN sur Open Food Facts. Ne lève jamais pour un EAN absent d'OFF (404, ou
 * `status` OFF différent de 1) ni pour une fiche sans composition exploitable : la forme du retour
 * (`OffLookupResult['status']`) porte la distinction, à charge de l'appelant de basculer sur le
 * chemin modèle pour `incomplete`/`not-found` (objectif § 2 et § 6).
 *
 * Lève en revanche pour toute autre erreur HTTP (429 — limitation de débit anticipée par
 * l'objectif § 3 —, 5xx, etc.) ou panne réseau : ce n'est PAS un « EAN inconnu », et le confondre
 * avec `not-found` inviterait l'utilisateur à créer un doublon d'une fiche qui existe peut-être.
 * L'appelant (main.ts) affiche alors un message « injoignable », sans proposer de contribution.
 */
export async function lookupOffProduct(ean: string, options: OffLookupOptions = {}): Promise<OffLookupResult> {
  const cache = options.cache ?? defaultCache();
  const cached = cache.get(ean);
  if (cached) {
    return toLookupResult(ean, cached);
  }

  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const baseUrl = options.baseUrl ?? 'https://world.openfoodfacts.org';
  const response = await fetchImpl(`${baseUrl}/api/v2/product/${encodeURIComponent(ean)}.json`, {
    headers: { 'User-Agent': OFF_USER_AGENT },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return cacheAndReturn(cache, ean, { status: 'not-found' });
    }
    throw new Error(`Open Food Facts a répondu ${response.status} pour ${ean}`);
  }

  const body = (await response.json()) as OffApiResponse;
  if (body.status !== 1 || !body.product) {
    return cacheAndReturn(cache, ean, { status: 'not-found' });
  }

  const product = mapOffProduct(ean, body.product);
  if (!product) {
    return cacheAndReturn(cache, ean, {
      status: 'incomplete',
      productName: readString(body.product, 'product_name'),
    });
  }

  return cacheAndReturn(cache, ean, { status: 'found', product });
}

function cacheAndReturn(cache: ProductCache, ean: string, entry: CachedProductLookup): OffLookupResult {
  cache.set(ean, entry);
  return toLookupResult(ean, entry);
}

function toLookupResult(ean: string, cached: CachedProductLookup): OffLookupResult {
  switch (cached.status) {
    case 'found':
      return { status: 'found', product: cached.product };
    case 'incomplete':
      return { status: 'incomplete', ean, productName: cached.productName, contributeUrl: offContributeUrl(ean) };
    case 'not-found':
      return { status: 'not-found', ean, contributeUrl: offContributeUrl(ean) };
  }
}
